import { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { CaretLeft, List, DotsThree, Trash, House, /* Heartbeat, */ Lightning, /* ForkKnife, Sparkle, */ X, Sun, Moon } from 'phosphor-react';
import { useTheme } from '../../hooks/useTheme';
import { MessageBubble, Message } from './MessageBubble';
import { InputBar, InputBarRef } from './InputBar';
import { WelcomeScreen } from './WelcomeScreen';
import { theme } from '../../styles/theme';
import { ActionType } from './ActionSheet';
import { FoodScanningModal } from './FoodScanningModal';
import { FoodFactsModal, FoodAnalysis } from './FoodFactsModal';
import { SideMenu } from './SideMenu';
import { DailyRoutinesView } from './DailyRoutinesView';
import { HealthMetricsView } from './HealthMetricsView';
import { CalorieTrackingView } from './CalorieTrackingView';
import { SkillsView, SkillInfo } from './SkillsView';
import { InsightsView } from './InsightsView';
import { WellnessPassSheet } from './WellnessPassSheet';
import { NutritionSurveyModal, SurveyCompletePayload } from './NutritionSurveyModal';
import { MentalHealthIntroModal } from './MentalHealthIntroModal';
import { useTranslation } from '../../i18n';
import { fileToBase64, resizeImage } from '../../utils/imageUtils';
import { buildAgentGreeting, buildRedirectGreeting, buildShortGreeting } from '../../utils/welcomeGreeting';
import { useAndroidKeyboard } from '../../hooks/useAndroidKeyboard';
import {
  trackAgentOpened,
  trackActionsPageOpen,
  trackConversationButton,
  trackOptionOffered,
  trackOptionSelected,
  trackSoulSatisfaction,
  derivePrimaryAgent,
  mapAgentIdToName,
} from '../../services/analytics.service';

interface UserInfo {
  userId: string;
  fullName: string;
  picturePath: string;
  initial: string;
}

interface ChatInterfaceProps {
  messages: Message[];
  onSendMessage: (content: string, imageUrl?: string, overrideAgent?: string) => void;
  isLoading?: boolean;
  onMessageAction?: (messageId: string | number, action: 'like' | 'dislike') => void;
  onSuggestionSelected?: (suggestion: string) => void;
  onBack?: () => void;
  onSessionLoad?: (sessionId: string) => Promise<void>;
  sessionId?: string;
  contentRecommendations?: any[];
  userInfo?: UserInfo | null;
  userProfileLoading?: boolean;
  token?: string;
  weeklyRoutines?: any;
  categorizedRoutines?: any;
  routinesLoading?: boolean;
  routinesGenerating?: boolean;
  onCompleteTask?: (date: string, taskId: string) => Promise<boolean>;
  availableSkills?: SkillInfo[];
  onSkillSelect?: (skillId: string) => void;
  activeSkillId?: string | null;
  onExitSkill?: () => void;
  skillSuggestions?: string[];
  selectedAgent?: string;
  onAgentChange?: (agent: string) => void;
  isConversationEnded?: boolean;
  mhWrapUp?: boolean;
  onMhWrapUpAction?: (action: 'continue' | 'end') => void;
  onMhEndConversation?: () => Promise<void>;
  clearMessages?: () => void;
  addAssistantMessage?: (content: string, options?: { hideActions?: boolean }) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 375px;
  margin: 0 auto;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.lg};
  padding-top: 32px; /* Account for status bar */
  flex-shrink: 0;
  position: relative;
  z-index: ${theme.zIndex.sticky};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const HeaderCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-subtle);
  padding: 3px;
  border-radius: 100px;
  height: 36px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 6px 10px;
  background: ${({ $active }) => ($active ? 'var(--color-border)' : 'transparent')};
  border-radius: 100px;
  color: ${({ $active }) => ($active ? 'var(--color-text)' : 'var(--color-text-secondary)')};
  transition: all ${theme.transitions.fast};
  border: none;
  cursor: pointer;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--color-border)' : 'var(--color-bg-elevated)')};
    color: var(--color-text);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const MenuButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-elevated);
  border-radius: ${theme.borderRadius.full};
  transition: all ${theme.transitions.fast};
  color: var(--color-text);

  &:hover {
    background: var(--color-border-subtle);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const BackButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-elevated);
  border-radius: ${theme.borderRadius.full};
  transition: all ${theme.transitions.fast};
  color: var(--color-text);

  &:hover {
    background: var(--color-border-subtle);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ProfileButton = styled.button<{ $isLoading?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  border: 2px solid var(--color-border);
  transition: all ${theme.transitions.fast};
  background: ${({ $isLoading }) => ($isLoading ? 'var(--color-bg-elevated)' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Playfair Display', serif;
  color: var(--color-text);

  &:hover {
    border-color: var(--color-primary);
  }

  &:active {
    transform: scale(0.95);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const ProfileLoadingSkeleton = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    var(--color-bg-elevated) 0%,
    var(--color-border) 50%,
    var(--color-bg-elevated) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const OptionsButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-elevated);
  border-radius: ${theme.borderRadius.full};
  transition: all ${theme.transitions.fast};
  color: var(--color-text);
  position: relative;

  &:hover {
    background: var(--color-border-subtle);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ContextMenuWrapper = styled.div`
  position: relative;
`;

const ContextMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 48px;
  right: 0;
  background: var(--color-card-bg);
  border: 1px solid var(--color-border-subtle);
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(20px);
  min-width: 200px;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(-10px)')};
  transition: all ${theme.transitions.fast};
  z-index: ${theme.zIndex.dropdown};
  overflow: hidden;
`;

const ContextMenuItem = styled.button`
  width: 100%;
  padding: 14px 16px;
  background: transparent;
  border: none;
  text-align: left;
  color: var(--color-text);
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: background ${theme.transitions.fast};
  cursor: pointer;

  &:hover {
    background: var(--color-bg-elevated);
  }

  &:active {
    background: var(--color-border-subtle);
  }

  svg {
    flex-shrink: 0;
  }
`;

const ContextMenuOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${theme.zIndex.dropdown - 1};
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
`;

const MessagesContainer = styled.div<{ $bottomPadding?: number }>`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing.lg};
  padding-bottom: ${({ $bottomPadding }) => ($bottomPadding != null ? $bottomPadding + 48 : 160)}px;
  display: flex;
  flex-direction: column;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-surface-dark);
    border-radius: ${theme.borderRadius.sm};
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};

  span {
    width: 8px;
    height: 8px;
    border-radius: ${theme.borderRadius.full};
    background: var(--color-text-secondary);
    animation: bounce 1.4s infinite;

    &:nth-of-type(2) {
      animation-delay: 0.2s;
    }

    &:nth-of-type(3) {
      animation-delay: 0.4s;
    }
  }

  @keyframes bounce {
    0%,
    80%,
    100% {
      transform: scale(0.8);
      opacity: 0.5;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const DialogOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: ${theme.zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const DialogBox = styled.div`
  background: var(--color-card-bg);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 24px;
  max-width: 320px;
  width: 100%;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
`;

const DialogTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 8px;
`;

const DialogMessage = styled.p`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 20px;
  line-height: 1.5;
`;

const DialogActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const DialogButton = styled.button<{ $variant?: 'danger' | 'default' }>`
  padding: 10px 20px;
  border-radius: 10px;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  border: 1px solid ${({ $variant }) => $variant === 'danger' ? '#EF4444' : 'var(--color-border)'};
  background: ${({ $variant }) => $variant === 'danger' ? '#EF4444' : 'transparent'};
  color: ${({ $variant }) => $variant === 'danger' ? '#FFFFFF' : 'var(--color-text)'};

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const WrapUpContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  background: var(--color-bg);
`;

const WrapUpButtonRow = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

const WrapUpActionsRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const WrapUpActionChip = styled.button`
  flex: 1 1 auto;
  min-width: 0;
  padding: 10px 12px;
  border-radius: 10px;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  border: 1px solid var(--color-border);
  background: var(--color-bg-elevated);
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:active {
    transform: scale(0.97);
    background: var(--color-bg);
  }
`;

const WrapUpButton = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 14px;
  border-radius: 14px;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  border: 1px solid ${({ $primary }) => $primary ? 'var(--color-primary)' : 'var(--color-border)'};
  background: ${({ $primary }) => $primary ? 'var(--color-primary)' : 'transparent'};
  color: ${({ $primary }) => $primary ? '#FFFFFF' : 'var(--color-text)'};

  &:active {
    transform: scale(0.97);
  }
`;

const RatingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px 20px;
  padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  background: var(--color-bg);
  position: relative;
`;

const RatingCloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 16px;
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.fast};

  &:hover { color: var(--color-text); }
  &:active { transform: scale(0.9); }
`;

const RatingTitle = styled.p`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text);
  text-align: center;
  margin: 0;
`;

const StarsRow = styled.div`
  display: flex;
  gap: 8px;
`;

const Star = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-size: 32px;
  color: ${({ $active }) => $active ? '#FFB800' : 'var(--color-border)'};
  transition: all 0.15s ease;
  line-height: 1;

  &:active {
    transform: scale(1.2);
  }
`;

const RatingThankYou = styled.p`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  color: var(--color-text-secondary);
  text-align: center;
  margin: 0;
`;

const ScrollAnchor = styled.div`
  height: 1px;
`;


const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;


// Mental-health approach selection chips (backend sends exactly one of these
// pairs). While they are on screen the chat input is hidden — the user must
// pick one of the two options. The input returns as soon as a choice is made
// (chipsHidden) or the suggestions reset (send/clear/load — i.e. leaving the
// chat or starting a new one).
const APPROACH_CHIP_DESCRIPTIONS: Record<string, string> = {
  'İçgörü Odaklı': 'Duygu ve düşüncelerini birlikte keşfedebiliriz.',
  'Çözüm Odaklı': 'Atabileceğin somut adımları birlikte değerlendirebiliriz.',
  'Reflective': 'We can explore your feelings and thoughts together.',
  'Solution-focused': 'We can consider concrete steps you can take together.',
};

const InputBarWrapper = styled.div<{ $bottomOffset: number }>`
  position: fixed;
  bottom: ${({ $bottomOffset }) => $bottomOffset}px;
  left: 0;
  right: 0;
  max-width: 375px;
  margin: 0 auto;
  z-index: 1000;
  background: linear-gradient(to bottom, transparent 0%, var(--color-bg) 12px);
  padding-top: 12px;
`;

const WelcomeHomeLayout = styled.div<{ $bottomPadding: number }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
  padding-bottom: ${({ $bottomPadding }) => $bottomPadding}px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: ${({ $bottomPadding }) => $bottomPadding}px;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(to top, var(--color-bg), transparent);
    pointer-events: none;
    z-index: 1;
  }
`;


const QuickRepliesContainer = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 16px;
  flex-wrap: wrap;
  background: var(--color-bg);
`;

const ApproachChoicesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 16px 12px;
  background: var(--color-bg);
`;

const ApproachChoiceButton = styled.button<{ $index: number }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 14px 16px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-subtle);
  border-radius: 14px;
  color: var(--color-text);
  font-family: ${theme.typography.fontFamily.primary};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: 0;
  animation: ${fadeInUp} 0.3s ease-out forwards;
  animation-delay: ${({ $index }) => $index * 80}ms;

  &:hover {
    background: var(--color-border-subtle);
    border-color: var(--color-border);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ApproachChoiceTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1.3;
`;

const ApproachChoiceDesc = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: var(--color-text-secondary);
  line-height: 1.4;
`;

const QuickReplyChip = styled.button<{ $index: number }>`
  padding: 8px 14px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-subtle);
  border-radius: 20px;
  color: var(--color-text);
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 13px;
  line-height: 1.3;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  opacity: 0;
  animation: ${fadeInUp} 0.3s ease-out forwards;
  animation-delay: ${({ $index }) => $index * 60}ms;

  &:hover {
    background: var(--color-border-subtle);
    border-color: var(--color-border);
  }

  &:active {
    transform: scale(0.97);
  }
`;



// Deep-link query param (?agent=...) → internal agent id. Lets the native
// app / external links open a specific assistant as if its welcome-screen
// card were tapped. Keys are matched case-insensitively, trimmed.
const AGENT_QUERY_MAP: Record<string, string> = {
  mentalhealth: 'mental-health',
  'mental-health': 'mental-health',
  mental: 'mental-health',
  therapy: 'mental-health',

  content: 'content-recommendation',
  'content-recommendation': 'content-recommendation',
  contentrecommendation: 'content-recommendation',
  suggestion: 'content-recommendation',
  recommendation: 'content-recommendation',

  faq: 'faq',
  support: 'faq',
  destek: 'faq',
};

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  onSendMessage,
  isLoading = false,
  onMessageAction,
  onSuggestionSelected: _onSuggestionSelected,
  onBack,
  onSessionLoad,
  sessionId,
  contentRecommendations: _contentRecommendations = [],
  userInfo,
  userProfileLoading = false,
  token,
  weeklyRoutines,
  categorizedRoutines,
  routinesLoading,
  routinesGenerating,
  onCompleteTask,
  availableSkills = [],
  onSkillSelect,
  activeSkillId,
  onExitSkill,
  skillSuggestions = [],
  selectedAgent = 'best',
  onAgentChange,
  isConversationEnded = false,
  mhWrapUp = false,
  onMhWrapUpAction,
  onMhEndConversation,
  clearMessages,
  addAssistantMessage,
}) => {
  const { t, lang } = useTranslation();
  const { mode: themeMode, toggle: toggleTheme } = useTheme();
  const [inputValue, setInputValue] = useState('');
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputBarRef = useRef<HTMLDivElement | null>(null);
  const inputBarResizeObserver = useRef<ResizeObserver | null>(null);
  // Callback ref: re-attach ResizeObserver every time the input bar (re)mounts
  const setInputBarRef = useCallback((node: HTMLDivElement | null) => {
    inputBarResizeObserver.current?.disconnect();
    inputBarRef.current = node;
    if (node) {
      setInputBarHeight(node.offsetHeight);
      const ro = new ResizeObserver(() => setInputBarHeight(node.offsetHeight));
      ro.observe(node);
      inputBarResizeObserver.current = ro;
    } else {
      inputBarResizeObserver.current = null;
    }
  }, []);
  const inputBarComponentRef = useRef<InputBarRef>(null);
  const [inputBarHeight, setInputBarHeight] = useState(0);
  const [chipsHidden, setChipsHidden] = useState(false);
  const [mhRating, setMhRating] = useState(0);
  const [mhRatingSubmitted, setMhRatingSubmitted] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ title: string; message: string; onConfirm: () => void; confirmLabel?: string; variant?: 'danger' | 'default'; onView?: () => void; viewLabel?: string } | null>(null);
  const [isScanningFood, setIsScanningFood] = useState(false);
  const [foodAnalysis, setFoodAnalysis] = useState<FoodAnalysis | null>(null);
  const [showFoodFacts, setShowFoodFacts] = useState(false);
  const [pendingFoodImage, setPendingFoodImage] = useState<string | null>(null);
  const [pendingFoodImageBase64, setPendingFoodImageBase64] = useState<string | null>(null);
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'home' | 'health' | 'actions' | 'calories' | 'skills' | 'insights'>('home');
  const [welcomeInputVisible, setWelcomeInputVisible] = useState(false);

  // Reset welcome input visibility when switching to a new session
  useEffect(() => {
    setWelcomeInputVisible(false);
  }, [sessionId]);

  // Track Option Offered when the two mental-health approach chips first appear.
  // Fires once per unique pair (not on every re-render).
  const approachOfferedRef = useRef<string | null>(null);
  useEffect(() => {
    const APPROACH_LABELS = new Set(['İçgörü Odaklı', 'Çözüm Odaklı', 'Reflective', 'Solution-focused']);
    const isApproachOffer =
      skillSuggestions.length === 2 &&
      skillSuggestions.every((s) => APPROACH_LABELS.has(s));
    if (!isApproachOffer) {
      approachOfferedRef.current = null;
      return;
    }
    const key = skillSuggestions.join('|');
    if (approachOfferedRef.current === key) return;
    approachOfferedRef.current = key;
    // Map chip labels (in order shown) to canonical approach names so the
    // Option Offered event reflects both options with a single call.
    const mapped = skillSuggestions.map((label) =>
      (label === 'İçgörü Odaklı' || label === 'Reflective') ? 'reflective' : 'solution focused'
    ) as Array<'reflective' | 'solution focused'>;
    trackOptionOffered(mapped);
  }, [skillSuggestions]);

  // Also reset when messages are cleared (e.g. user hits back from an active chat
  // on the home tab). Without this, the input bar overlay can remain mounted
  // while the WelcomeScreen is shown behind it.
  useEffect(() => {
    if (messages.length === 0 && activeTab === 'home' && !activeSkillId) {
      setWelcomeInputVisible(false);
    }
  }, [messages.length, activeTab, activeSkillId]);
  const [showNutritionSurvey, setShowNutritionSurvey] = useState(false);
  const [showMhIntro, setShowMhIntro] = useState(false);
  const [calorieRefreshKey, setCalorieRefreshKey] = useState(0);
  const [wellnessPassUrl, setWellnessPassUrl] = useState<string | null>(null);
  const [iframeSheet, setIframeSheet] = useState<{ url: string; title: string } | null>(null);
  const [hasSurveyChecked, setHasSurveyChecked] = useState(false);
  const keyboardOffset = useAndroidKeyboard();

  // Auto-open nutrition survey when calories tab is first opened and user has no survey
  useEffect(() => {
    if (activeTab !== 'calories' || !userInfo?.userId || hasSurveyChecked) return;
    setHasSurveyChecked(true);

    const apiUrl = (import.meta as any).env?.VITE_API_URL || '/api';
    fetch(`${apiUrl}/chat/nutrition-survey/${userInfo.userId}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && !data.data.hasSurvey) {
          setShowNutritionSurvey(true);
        }
      })
      .catch(() => {});
  }, [activeTab, userInfo?.userId, hasSurveyChecked]);

  // Android keyboard offset handling lives in useAndroidKeyboard.

  // InputBar height is measured via setInputBarRef callback ref above
  // (handles re-mount when switching between welcome and chat views).

  const WELLNESSPASS_BASE = 'https://wellnesspass-app.meditopia.com';

  const handleStudioClick = useCallback((studioId: string) => {
    setWellnessPassUrl(`${WELLNESSPASS_BASE}/studios/${studioId}?token=${token || ''}&theme=dark`);
  }, [token]);

  const handleEventClick = useCallback((eventId: string) => {
    setWellnessPassUrl(`${WELLNESSPASS_BASE}/live-classes/${eventId}?token=${token || ''}&theme=dark`);
  }, [token]);

  // Intercept clicks on "internal" assistant links (1-1 therapy webapp,
  // WellnessPass webapp) and rewrite them to the Meditopia internalwebview
  // wrapper so the native WebView handler picks them up with a fresh user
  // token.
  //
  // Final URL shapes:
  //   https://www.meditopia.com/en/internalwebview?id=https://webapp.meditopia.com/tr/therapy?app=1&token={TOKEN}
  //   https://www.meditopia.com/en/internalwebview?id=https://wellnesspass-app.meditopia.com/?locale={LANG}&token={TOKEN}
  //
  // The token is appended per-click and never persisted to the message / DB.
  // Returns true when the click was handled (caller must preventDefault).
  const handleInternalLinkClick = useCallback((url: string): boolean => {
    const therapyRegex = /https?:\/\/webapp\.meditopia\.com\/(tr|en)\/therapy[^\s"'<>]*/i;
    const wellnessPassRegex = /https?:\/\/wellnesspass-app\.meditopia\.com\/?[^\s"'<>]*/i;
    const supportResourcesRegex = /https?:\/\/(?:www\.)?meditopia\.com\/(tr|en)\/destekal\/?[^\s"'<>]*/i;

    // Crisis-resources link from the safety message: WKWebView blocks the
    // default new-window navigation, so the URL is unreachable as a plain
    // anchor. Open it in the in-app iframe sheet (same component used for
    // other internal pages) so the resources page actually loads.
    const supportMatch = supportResourcesRegex.exec(url);
    if (supportMatch) {
      const matchedLang = supportMatch[1].toLowerCase();
      const title = matchedLang === 'tr' ? 'Destek Al' : 'Get Support';
      setIframeSheet({ url, title });
      return true;
    }

    // WellnessPass plan/package page: answers about the user's own package
    // (usage limit, remaining entries, renewal) link here. Opened in the same
    // closable sheet as studio and live-class cards, so the user lands back in
    // the chat when they close it.
    if (/^https?:\/\/wellnesspass-app\.meditopia\.com\/profile\/?(\?|#|$)/i.test(url)) {
      setWellnessPassUrl(`${WELLNESSPASS_BASE}/profile?token=${token || ''}&theme=dark`);
      return true;
    }

    const safeLang = lang === 'tr' ? 'tr' : 'en';
    let innerUrl: string | null = null;

    if (therapyRegex.test(url)) {
      innerUrl = `https://webapp.meditopia.com/tr/therapy?app=1&token=${token || ''}`;
    } else if (wellnessPassRegex.test(url)) {
      innerUrl = `https://wellnesspass-app.meditopia.com/?locale=${safeLang}&token=${token || ''}`;
    }

    if (!innerUrl) return false;

    // The inner URL carries its own query string (app=1, locale, token, etc.)
    // so it MUST be percent-encoded — otherwise the '&' delimiters would be
    // parsed as additional params of the outer wrapper URL instead of the
    // inner destination.
    const wrapperUrl = `https://www.meditopia.com/en/internalwebview?id=${encodeURIComponent(innerUrl)}`;
    // Navigate the current window — in iOS WebView this is intercepted by the
    // native handler, on desktop it opens the destination in the same tab.
    window.location.href = wrapperUrl;
    return true;
  }, [token, lang]);

  // Auto-scroll to bottom when new messages arrive, suggestions appear, or input bar resizes
  useEffect(() => {
    // Small delay to let chips render and inputBarHeight update via ResizeObserver
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [messages, inputBarHeight, skillSuggestions]);

  // Reset chipsHidden when new chips arrive
  useEffect(() => {
    if (skillSuggestions.length > 0) {
      setChipsHidden(false);
    }
  }, [skillSuggestions]);

  // While the mental-health approach choice is on screen, the chat input is
  // hidden — the user must pick one of the two options. Derived (not stored)
  // on purpose: skillSuggestions is cleared on send/clearMessages/loadMessages,
  // so the input automatically comes back after a choice, when the user leaves
  // this chat, or when a new chat starts.
  const isApproachGateActive =
    skillSuggestions.length === 2 &&
    skillSuggestions.every((s) => s in APPROACH_CHIP_DESCRIPTIONS) &&
    !chipsHidden &&
    !isLoading;

  const handleSend = async () => {
    if ((inputValue.trim() || selectedImageFile) && !isLoading) {
      let imageDataUrl: string | undefined;

      // If there's an image, convert to base64 data URL
      if (selectedImageFile) {
        imageDataUrl = await fileToBase64(selectedImageFile);
      }

      onSendMessage(inputValue.trim() || 'What\'s in this image?', imageDataUrl);
      setInputValue('');
      setSelectedImageFile(null);
      setSelectedImagePreview(null);
    }
  };

  const handleImageSelect = async (file: File, isFromFoodCapture: boolean = false) => {
    setSelectedImageFile(file);
    // Replace (and free) any previous preview blob URL, then create a new one
    if (selectedImagePreview) URL.revokeObjectURL(selectedImagePreview);
    const previewUrl = URL.createObjectURL(file);
    setSelectedImagePreview(previewUrl);
    // If this is from food capture action, analyze the food
    if (isFromFoodCapture) {
      await analyzeFoodImage(file, previewUrl);
    }
  };

  const analyzeFoodImage = async (file: File, previewUrl: string) => {
    try {
      // Resize and compress image for OpenAI API
      const base64Image = await resizeImage(file, 1024, 1024, 0.8);

      // Show scanning modal with preview
      setIsScanningFood(true);
      setPendingFoodImage(previewUrl);
      setPendingFoodImageBase64(base64Image);

      const response = await fetch('/api/chat/analyze-food', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          imageUrl: base64Image,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to analyze food');
      }

      setIsScanningFood(false);
      setFoodAnalysis(data.data);
      setShowFoodFacts(true);
    } catch (error: any) {
      console.error('Error analyzing food:', error);
      setIsScanningFood(false);

      setConfirmDialog({
        title: lang === 'tr' ? 'Analiz Başarısız' : 'Analysis Failed',
        message: error.message || (lang === 'tr' ? 'Yemek analiz edilemedi. Lütfen tekrar dene.' : 'Failed to analyze food. Please try again.'),
        confirmLabel: 'OK',
        onConfirm: () => setConfirmDialog(null),
      });

      // Clean up the image (and free the blob URL)
      URL.revokeObjectURL(previewUrl);
      setSelectedImageFile(null);
      setSelectedImagePreview(null);
      setPendingFoodImage(null);
      setPendingFoodImageBase64(null);
    }
  };

  const handleImageRemove = () => {
    if (selectedImagePreview) {
      URL.revokeObjectURL(selectedImagePreview);
    }
    setSelectedImageFile(null);
    setSelectedImagePreview(null);
  };

  // handleBack function removed - not currently used
  // Can be re-added when back button is implemented

  const handleProfile = () => {
    // Send message to native app to open profile
    if (window.webkit?.messageHandlers?.soulBridge) {
      window.webkit.messageHandlers.soulBridge.postMessage({
        type: 'navigation:profile',
      });
    }
  };

  // Latest-ref wrappers: the parent (App) re-renders on every streamed token,
  // so callback props can change identity constantly. Routing them through
  // refs lets us hand stable callbacks to the memoized MessageBubble list —
  // otherwise every bubble re-renders on every token.
  const onMessageActionRef = useRef(onMessageAction);
  const onAgentChangeRef = useRef(onAgentChange);
  const clearMessagesRef = useRef(clearMessages);
  const addAssistantMessageRef = useRef(addAssistantMessage);
  const userInfoRef = useRef(userInfo);
  useEffect(() => {
    onMessageActionRef.current = onMessageAction;
    onAgentChangeRef.current = onAgentChange;
    clearMessagesRef.current = clearMessages;
    addAssistantMessageRef.current = addAssistantMessage;
    userInfoRef.current = userInfo;
  });

  const handleLike = useCallback((messageId: string | number) => {
    onMessageActionRef.current?.(messageId, 'like');
  }, []);

  const handleDislike = useCallback((messageId: string | number) => {
    onMessageActionRef.current?.(messageId, 'dislike');
  }, []);

  const handleRedirectToAgent = useCallback((agentId: string) => {
    clearMessagesRef.current?.();
    onAgentChangeRef.current?.(agentId);
    setWelcomeInputVisible(true);
    const firstName = userInfoRef.current?.fullName?.split(' ')[0] || '';
    const { hello, intro } = buildRedirectGreeting(agentId, firstName, lang);
    addAssistantMessageRef.current?.(hello, { hideActions: true });
    // Slight delay so the second message renders as a separate bubble.
    setTimeout(() => addAssistantMessageRef.current?.(intro, { hideActions: true }), 60);
  }, [lang]);

  // Open an assistant exactly as if its welcome-screen card were tapped.
  // Shared by the WelcomeScreen cards and the ?agent= deep link.
  const handleAgentSelect = useCallback((agentId: string) => {
    const agentName = mapAgentIdToName(agentId);
    if (agentName) trackAgentOpened(agentName);

    // First-time mental-health users get a one-off informational popup
    // explaining the assistant and the two approach options.
    if (agentId === 'mental-health') {
      const MH_INTRO_KEY = 'soul-mh-intro-seen';
      if (!localStorage.getItem(MH_INTRO_KEY)) {
        localStorage.setItem(MH_INTRO_KEY, '1');
        setShowMhIntro(true);
      }
    }

    onAgentChangeRef.current?.(agentId);
    setActiveTab('home');
    setWelcomeInputVisible(true);

    // Stock welcome greeting (texts in utils/welcomeGreeting.ts) — long form
    // for the user's first 4 chat sessions, short form thereafter.
    const firstName = userInfoRef.current?.fullName?.split(' ')[0] || '';
    const STORAGE_KEY = 'soul-welcome-count';
    const prevCount = Number(localStorage.getItem(STORAGE_KEY) || '0');
    localStorage.setItem(STORAGE_KEY, String(prevCount + 1));

    if (prevCount < 4) {
      const { hello, intro } = buildAgentGreeting(agentId, firstName, lang);
      addAssistantMessageRef.current?.(hello, { hideActions: true });
      if (intro) {
        // Slight delay so the second message renders as a separate bubble.
        setTimeout(() => addAssistantMessageRef.current?.(intro, { hideActions: true }), 60);
      }
    } else {
      addAssistantMessageRef.current?.(buildShortGreeting(agentId, firstName, lang), { hideActions: true });
    }
  }, [lang]);

  // Deep-link: ?agent=mentalhealth (etc.) opens that assistant on load, as if
  // the user tapped its card. Fires once, after the profile loads so the
  // greeting can be personalized with the user's name.
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (deepLinkHandledRef.current || userProfileLoading) return;
    const raw = new URLSearchParams(window.location.search).get('agent');
    if (!raw) return;
    const agentId = AGENT_QUERY_MAP[raw.trim().toLowerCase()];
    if (!agentId) return;
    deepLinkHandledRef.current = true;
    handleAgentSelect(agentId);
  }, [userProfileLoading, handleAgentSelect]);


  const handleActionSelect = (action: ActionType) => {
    switch (action) {
      // Pre-chat actions
      case 'find-gym':
        onSendMessage(t('actions.findGymPrompt'));
        break;
      case 'content-suggestion':
        onSendMessage(t('actions.contentSuggestionPrompt'));
        break;
      case 'nutrition-survey':
        setShowNutritionSurvey(true);
        break;

      // In-chat actions
      case 'end-conversation':
        trackConversationButton('end conversation');
        handleEndConversation();
        break;
      case 'suggest-content':
        // Force content-recommendation agent so the request directly triggers
        // the recommendation flow instead of being routed by the LLM.
        trackConversationButton('content suggestion');
        onSendMessage(t('chatActions.suggestContentPrompt'), undefined, 'content-recommendation');
        break;
      case 'talk-to-expert':
        trackConversationButton('1-1 guidance');
        onSendMessage(t('chatActions.talkToExpertPrompt'));
        break;
      case 'generate-insights':
        trackConversationButton('insight');
        handleGenerateInsights();
        break;

      default:
        break;
    }
  };

  const handleEndConversation = () => {
    setConfirmDialog({
      title: t('chatActions.endConversation').replace('\n', ' '),
      message: t('chatActions.endConfirm'),
      confirmLabel: t('chatActions.endConfirmYes'),
      variant: 'danger',
      onConfirm: () => {
        setConfirmDialog(null);
        // Not enough content to summarize — just close the chat
        if (messages.length < 4) {
          clearMessages?.();
          setActiveTab('home');
          setWelcomeInputVisible(false);
          onAgentChange?.('best');
        } else {
          onSendMessage('__END_CONVERSATION__');
        }
      },
    });
  };

  const handleGenerateInsights = async () => {
    if (!sessionId || !userInfo?.userId) return;

    // Show loading state in dialog
    setConfirmDialog({
      title: lang === 'tr' ? 'İçgörüler çıkarılıyor...' : 'Extracting insights...',
      message: lang === 'tr'
        ? 'Konuşmandan içgörüler ve eylem adımları çıkarılıyor, lütfen bekle.'
        : 'Extracting insights and actionable items from your conversation, please wait.',
      confirmLabel: '...',
      onConfirm: () => {},
    });

    try {
      const response = await fetch('/api/chat/insights/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          userId: userInfo.userId,
          sessionId,
          lang,
        }),
      });

      if (!response.ok) throw new Error('Failed to generate insights');
      const data = await response.json();
      const count = data.data?.count || 0;

      if (count === 0) {
        setConfirmDialog({
          title: lang === 'tr' ? 'İçgörü Bulunamadı' : 'No Insights',
          message: lang === 'tr'
            ? 'Bu konuşmadan anlamlı bir içgörü çıkaramadım. Biraz daha derinlemesine konuştuktan sonra tekrar dene.'
            : 'I couldn\'t extract meaningful insights from this conversation yet. Try again after talking a bit more.',
          confirmLabel: 'OK',
          onConfirm: () => setConfirmDialog(null),
        });
        return;
      }

      setConfirmDialog({
        title: lang === 'tr' ? '✓ Kaydedildi' : '✓ Saved',
        message: lang === 'tr'
          ? `${count} içgörü eklendi. "İçgörülerim" menüsünden görebilirsin.`
          : `${count} insights added. View them in the "My Insights" menu.`,
        confirmLabel: 'OK',
        onConfirm: () => setConfirmDialog(null),
        viewLabel: lang === 'tr' ? 'Gör' : 'View',
        onView: () => {
          setConfirmDialog(null);
          if (messages.length > 0) clearMessages?.();
          setActiveTab('insights');
        },
      });
    } catch (e) {
      console.error('Error generating insights:', e);
      setConfirmDialog({
        title: lang === 'tr' ? 'Hata' : 'Error',
        message: lang === 'tr'
          ? 'İçgörüler oluşturulurken bir hata oluştu.'
          : 'Something went wrong while generating insights.',
        confirmLabel: 'OK',
        onConfirm: () => setConfirmDialog(null),
      });
    }
  };

  const handleTalkWithSoul = () => {
    // Close food facts modal
    setShowFoodFacts(false);

    // Send nutritional info to chat
    if (foodAnalysis) {
      const message = `I just had ${foodAnalysis.name}. It has ${foodAnalysis.calories} kcal, with ${foodAnalysis.macros.protein.grams}g protein, ${foodAnalysis.macros.carbs.grams}g carbs, and ${foodAnalysis.macros.fat.grams}g fat.`;
      // Use base64 image instead of blob URL so OpenAI can process it
      onSendMessage(message, pendingFoodImageBase64 || undefined);

      // Clear states (and free the preview blob URL)
      if (pendingFoodImage) URL.revokeObjectURL(pendingFoodImage);
      setFoodAnalysis(null);
      setPendingFoodImage(null);
      setPendingFoodImageBase64(null);
      setSelectedImageFile(null);
      setSelectedImagePreview(null);
    }
  };

  const handleMenuToggle = () => {
    setIsSideMenuOpen(!isSideMenuOpen);
  };

  const handleSessionSelect = async (sessionId: string) => {
    if (!onSessionLoad) {
      console.warn('[ChatInterface] onSessionLoad not provided');
      return;
    }

    try {
      await onSessionLoad(sessionId);
    } catch (error) {
      console.error('[ChatInterface] Error loading session:', error);
      setConfirmDialog({
        title: lang === 'tr' ? 'Hata' : 'Error',
        message: lang === 'tr' ? 'Konuşma yüklenemedi. Lütfen tekrar dene.' : 'Failed to load conversation. Please try again.',
        confirmLabel: 'OK',
        onConfirm: () => setConfirmDialog(null),
      });
    }
  };

  const handleDeleteConversation = async () => {
    if (!sessionId) {
      console.warn('[ChatInterface] No sessionId to delete');
      return;
    }

    setIsContextMenuOpen(false);
    setConfirmDialog({
      title: t('chat.deleteConversation'),
      message: lang === 'tr' ? 'Bu konuşmayı silmek istediğine emin misin?' : 'Are you sure you want to delete this conversation?',
      confirmLabel: lang === 'tr' ? 'Sil' : 'Delete',
      variant: 'danger',
      onConfirm: () => { setConfirmDialog(null); executeDeleteConversation(); },
    });
  };

  const executeDeleteConversation = async () => {
    if (!sessionId) return;

    try {

      const response = await fetch(`/api/chat/sessions/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete conversation');
      }

      // Go back to welcome screen
      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error('[ChatInterface] Error deleting conversation:', error);
      setConfirmDialog({
        title: lang === 'tr' ? 'Hata' : 'Error',
        message: lang === 'tr' ? 'Konuşma silinemedi. Lütfen tekrar dene.' : 'Failed to delete conversation. Please try again.',
        confirmLabel: 'OK',
        onConfirm: () => setConfirmDialog(null),
      });
    }
  };

  return (
    <>
      <FoodScanningModal isOpen={isScanningFood} imageUrl={pendingFoodImage || ''} />
      <FoodFactsModal
        isOpen={showFoodFacts}
        onClose={() => setShowFoodFacts(false)}
        foodData={foodAnalysis}
        onTalkWithSoul={handleTalkWithSoul}
      />
      <SideMenu
        isOpen={isSideMenuOpen}
        onClose={() => setIsSideMenuOpen(false)}
        onSessionSelect={handleSessionSelect}
        onNavigate={(destination) => {
          if (destination === 'insights') {
            if (messages.length > 0) {
              clearMessages?.();
            }
            setActiveTab('insights');
          }
        }}
        userId={userInfo?.userId}
      />
      <WellnessPassSheet
        isOpen={!!wellnessPassUrl}
        onClose={() => setWellnessPassUrl(null)}
        url={wellnessPassUrl || ''}
      />
      <WellnessPassSheet
        isOpen={!!iframeSheet}
        onClose={() => setIframeSheet(null)}
        url={iframeSheet?.url || ''}
        title={iframeSheet?.title}
      />
      <MentalHealthIntroModal
        isOpen={showMhIntro}
        onClose={() => setShowMhIntro(false)}
      />
      <NutritionSurveyModal
        isOpen={showNutritionSurvey}
        onClose={() => setShowNutritionSurvey(false)}
        userId={userInfo?.userId || ''}
        lang={lang}
        onComplete={(payload: SurveyCompletePayload | null) => {
          setShowNutritionSurvey(false);
          setHasSurveyChecked(false);
          setCalorieRefreshKey(k => k + 1);
          if (!payload) {
            onSendMessage(t('nutritionSurvey.successMessage'));
            return;
          }
          const { result, previousSurvey, isUpdate } = payload;
          if (isUpdate && previousSurvey && result) {
            const weightDiff = (result.weightKg - previousSurvey.weightKg).toFixed(1);
            const weightDir = Number(weightDiff) < 0 ? 'lost' : Number(weightDiff) > 0 ? 'gained' : 'same';
            const prevCal = Math.round(previousSurvey.recommendedCalories);
            const newCal = Math.round(result.recommendedCalories);
            const lines = [
              lang === 'tr'
                ? `Beslenme anketimi güncelledim.`
                : `I just updated my nutrition survey.`,
              lang === 'tr'
                ? `Önceki kilom: ${previousSurvey.weightKg} kg → Şu anki kilom: ${result.weightKg} kg (${weightDir === 'lost' ? `${Math.abs(Number(weightDiff))} kg verdim` : weightDir === 'gained' ? `${weightDiff} kg aldım` : 'değişmedi'}).`
                : `Previous weight: ${previousSurvey.weightKg} kg → Current weight: ${result.weightKg} kg (${weightDir === 'lost' ? `lost ${Math.abs(Number(weightDiff))} kg` : weightDir === 'gained' ? `gained ${weightDiff} kg` : 'no change'}).`,
              prevCal !== newCal
                ? (lang === 'tr'
                    ? `Kalori hedefim ${prevCal} kcal'den ${newCal} kcal'e güncellendi.`
                    : `My calorie goal was updated from ${prevCal} kcal to ${newCal} kcal.`)
                : '',
              result.targetWeightKg && previousSurvey.targetWeightKg && result.targetWeightKg !== previousSurvey.targetWeightKg
                ? (lang === 'tr'
                    ? `Hedef kilom ${previousSurvey.targetWeightKg} kg'dan ${result.targetWeightKg} kg'a değişti.`
                    : `My target weight changed from ${previousSurvey.targetWeightKg} kg to ${result.targetWeightKg} kg.`)
                : '',
              lang === 'tr'
                ? `Bu değişikliklere göre ilerleme analizimi yap ve güncellenmiş bir yemek planı öner. Motivasyonumu yüksek tut!`
                : `Based on these changes, analyze my progress and suggest an updated meal plan. Keep me motivated!`,
            ].filter(Boolean).join(' ');
            onSendMessage(lines);
          } else {
            onSendMessage(t('nutritionSurvey.planRequest'));
          }
        }}
      />

      <Container>
        <Header>
          <HeaderLeft>
            {messages.length === 0 && !activeSkillId ? (
              <MenuButton onClick={handleMenuToggle} aria-label="Open menu">
                <List size={24} weight="bold" />
              </MenuButton>
            ) : (
              <BackButton onClick={activeSkillId ? onExitSkill : onBack} aria-label="Go back">
                <CaretLeft size={24} weight="bold" />
              </BackButton>
            )}
          </HeaderLeft>

          {messages.length === 0 && !activeSkillId && (
            <HeaderCenter>
              <TabButton $active={activeTab === 'home'} onClick={() => { setActiveTab('home'); setWelcomeInputVisible(false); clearMessages?.(); onAgentChange?.('best'); }}>
                <House size={18} weight={activeTab === 'home' ? 'fill' : 'regular'} />
              </TabButton>
              {/* Health tab hidden - feature in beta, will re-enable later */}
              {/* <TabButton $active={activeTab === 'health'} onClick={() => setActiveTab('health')}>
                <Heartbeat size={18} weight={activeTab === 'health' ? 'fill' : 'regular'} />
              </TabButton> */}
              <TabButton $active={activeTab === 'actions'} onClick={() => { trackActionsPageOpen(); setActiveTab('actions'); }}>
                <Lightning size={18} weight={activeTab === 'actions' ? 'fill' : 'regular'} />
              </TabButton>
              {/* Calories and Skills tabs hidden for initial prod release
              <TabButton $active={activeTab === 'calories'} onClick={() => setActiveTab('calories')}>
                <ForkKnife size={18} weight={activeTab === 'calories' ? 'fill' : 'regular'} />
              </TabButton>
              <TabButton $active={activeTab === 'skills'} onClick={() => setActiveTab('skills')}>
                <Sparkle size={18} weight={activeTab === 'skills' ? 'fill' : 'regular'} />
              </TabButton>
              */}
            </HeaderCenter>
          )}

          <HeaderRight>
            {messages.length > 0 && (
              <>
                <ContextMenuOverlay
                  $isOpen={isContextMenuOpen}
                  onClick={() => setIsContextMenuOpen(false)}
                />
                <ContextMenuWrapper>
                  <OptionsButton
                    onClick={() => setIsContextMenuOpen(!isContextMenuOpen)}
                    aria-label="Open options"
                  >
                    <DotsThree size={24} weight="bold" />
                  </OptionsButton>
                  <ContextMenu $isOpen={isContextMenuOpen}>
                    <ContextMenuItem onClick={() => { toggleTheme(); setIsContextMenuOpen(false); }}>
                      {themeMode === 'dark' ? <Sun size={20} weight="bold" /> : <Moon size={20} weight="bold" />}
                      {themeMode === 'dark' ? t('chat.lightMode') : t('chat.darkMode')}
                    </ContextMenuItem>
                    <ContextMenuItem onClick={handleDeleteConversation}>
                      <Trash size={20} weight="bold" />
                      {t('chat.deleteConversation')}
                    </ContextMenuItem>
                  </ContextMenu>
                </ContextMenuWrapper>
              </>
            )}

            <ProfileButton
              onClick={handleProfile}
              aria-label="Open profile"
              $isLoading={userProfileLoading}
            >
              {userProfileLoading ? (
                <ProfileLoadingSkeleton />
              ) : userInfo?.picturePath ? (
                <img src={userInfo.picturePath} alt={userInfo.fullName} />
              ) : (
                userInfo?.initial || 'U'
              )}
            </ProfileButton>
          </HeaderRight>
        </Header>

      {messages.length === 0 && activeTab === 'home' && !activeSkillId && !welcomeInputVisible ? (
        <WelcomeHomeLayout $bottomPadding={0}>
          <WelcomeScreen
            userName={userInfo?.fullName.split(' ')[0] || 'User'}
            isLoading={userProfileLoading}
            routinesLoading={routinesLoading}
            categorizedRoutines={categorizedRoutines}
            onCompleteTask={onCompleteTask}
            token={token}
            onAgentSelect={handleAgentSelect}
          />
        </WelcomeHomeLayout>
      ) : (
        <MessagesContainer
          ref={messagesContainerRef}
          $bottomPadding={
            // InputBar is only rendered on home/health/active-skill. Other
            // tabs (actions, calories, skills, insights) have no input bar —
            // drop the reserved height so content isn't cut off by a stale
            // inputBarHeight measurement.
            activeTab === 'home' || activeTab === 'health' || activeSkillId
              ? inputBarHeight
              : 0
          }
        >
          {messages.length === 0 ? (
            activeTab === 'health' ? (
              <HealthMetricsView userId={userInfo?.userId} token={token} />
            ) : activeTab === 'actions' ? (
              <DailyRoutinesView
                weeklyRoutines={weeklyRoutines || {}}
                categorizedRoutines={categorizedRoutines || {}}
                isLoading={routinesLoading || false}
                isGenerating={routinesGenerating || false}
                onCompleteTask={(date, taskId) => {
                  if (onCompleteTask) {
                    onCompleteTask(date, taskId);
                  }
                }}
                token={token}
              />
            ) : activeTab === 'calories' ? (
              <CalorieTrackingView userId={userInfo?.userId} token={token} refreshKey={calorieRefreshKey} onSendMessage={(msg) => { onSendMessage(msg); setActiveTab('home'); }} />
            ) : activeTab === 'skills' ? (
              <SkillsView
                skills={availableSkills}
                onSkillSelect={(skillId) => onSkillSelect?.(skillId)}
              />
            ) : activeTab === 'insights' ? (
              <InsightsView userId={userInfo?.userId} />
            ) : null
          ) : (
            <>
              {messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  onLike={handleLike}
                  onDislike={handleDislike}
                  showActions={message.role === 'assistant' && !message.hideActions}
                  onStudioClick={handleStudioClick}
                  onEventClick={handleEventClick}
                  onInternalLinkClick={handleInternalLinkClick}
                  onRedirectToAgent={handleRedirectToAgent}
                />
              ))}

              {isLoading && (
                <TypingIndicator>
                  <span />
                  <span />
                  <span />
                </TypingIndicator>
              )}
            </>
          )}

          <ScrollAnchor ref={messagesEndRef} />
        </MessagesContainer>
      )}

      {(activeTab === 'home' || activeTab === 'health' || activeSkillId) && !isConversationEnded && !(messages.length === 0 && activeTab === 'home' && !activeSkillId && !welcomeInputVisible) && (
        mhWrapUp ? (
          mhRatingSubmitted ? (
            <RatingContainer>
              <RatingThankYou>{lang === 'tr' ? 'Değerlendirmen için teşekkürler!' : 'Thanks for your feedback!'}</RatingThankYou>
              <WrapUpButton $primary onClick={() => { clearMessages?.(); setMhRatingSubmitted(false); setMhRating(0); }}>
                {lang === 'tr' ? 'Yeni Konuşma Başlat' : 'Start New Conversation'}
              </WrapUpButton>
            </RatingContainer>
          ) : mhRating !== 0 ? (
            <RatingContainer>
              <RatingCloseButton onClick={async () => {
                await onMhEndConversation?.();
                setMhRatingSubmitted(true);
              }}>
                <X size={20} weight="bold" />
              </RatingCloseButton>
              <RatingTitle>{lang === 'tr' ? 'Bu konuşmayı nasıl değerlendirirsin?' : 'How would you rate this conversation?'}</RatingTitle>
              <StarsRow>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} $active={star <= mhRating} onClick={async () => {
                    setMhRating(star);
                    // Save rating immediately on star click
                    try {
                      const apiUrl = import.meta.env.VITE_API_URL || '/api';
                      await fetch(`${apiUrl}/chat/sessions/${sessionId}/rate`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ rating: star }),
                      });
                    } catch (e) {
                      console.error('Error saving rating:', e);
                    }
                    trackSoulSatisfaction(star as 1 | 2 | 3 | 4 | 5, {
                      agentType: derivePrimaryAgent(
                        messages
                          .filter((m) => m.role === 'assistant')
                          .map((m) => m.agent)
                      ),
                    });
                    await onMhEndConversation?.();
                    setMhRatingSubmitted(true);
                  }}>
                    {star <= mhRating ? '★' : '☆'}
                  </Star>
                ))}
              </StarsRow>
            </RatingContainer>
          ) : (
            <WrapUpContainer>
              <WrapUpActionsRow>
                <WrapUpActionChip
                  onClick={() => {
                    onSendMessage(t('chatActions.suggestContentPrompt'), undefined, 'content-recommendation');
                  }}
                >
                  {lang === 'tr' ? 'İçerik Önerisi Al' : 'Suggest Content'}
                </WrapUpActionChip>
                <WrapUpActionChip
                  onClick={() => {
                    onSendMessage(t('chatActions.talkToExpertPrompt'));
                  }}
                >
                  {lang === 'tr' ? 'Uzmanla Görüş' : 'Talk to Expert'}
                </WrapUpActionChip>
                <WrapUpActionChip onClick={handleGenerateInsights}>
                  {lang === 'tr' ? 'İçgörü Al ve Kaydet' : 'Get Insights'}
                </WrapUpActionChip>
              </WrapUpActionsRow>
              <WrapUpButtonRow>
                <WrapUpButton onClick={() => onMhWrapUpAction?.('continue')}>
                  {lang === 'tr' ? 'Konuşmaya Devam Et' : 'Continue Conversation'}
                </WrapUpButton>
                <WrapUpButton $primary onClick={() => setMhRating(-1)}>
                  {lang === 'tr' ? 'Konuşmayı Bitir' : 'End Conversation'}
                </WrapUpButton>
              </WrapUpButtonRow>
            </WrapUpContainer>
          )
        ) : (
          <InputBarWrapper ref={setInputBarRef} $bottomOffset={keyboardOffset}>
            {skillSuggestions.length > 0 && !isLoading && !chipsHidden && (() => {
              // Special rendering for the mental-health approach selection.
              // Backend sends exactly these two labels — we render them as
              // stacked, two-line rich buttons (title + description) instead
              // of the default horizontal chip row.
              if (isApproachGateActive) {
                return (
                  <ApproachChoicesContainer>
                    {skillSuggestions.map((text, idx) => (
                      <ApproachChoiceButton
                        key={idx}
                        $index={idx}
                        onClick={() => {
                          const approachType = (text === 'İçgörü Odaklı' || text === 'Reflective') ? 'reflective' : 'solution focused';
                          trackOptionSelected(approachType);
                          setChipsHidden(true);
                          onSendMessage(text);
                        }}
                      >
                        <ApproachChoiceTitle>{text}</ApproachChoiceTitle>
                        <ApproachChoiceDesc>{APPROACH_CHIP_DESCRIPTIONS[text]}</ApproachChoiceDesc>
                      </ApproachChoiceButton>
                    ))}
                  </ApproachChoicesContainer>
                );
              }

              return (
                <QuickRepliesContainer>
                  {skillSuggestions.map((text, idx) => (
                    <QuickReplyChip
                      key={idx}
                      $index={idx}
                      onClick={() => { setChipsHidden(true); onSendMessage(text); }}
                    >
                      {text}
                    </QuickReplyChip>
                  ))}
                </QuickRepliesContainer>
              );
            })()}
            {!isApproachGateActive && (
              <InputBar
                ref={inputBarComponentRef}
                value={inputValue}
                onChange={setInputValue}
                onSend={handleSend}
                disabled={isLoading}
                placeholder={activeSkillId ? t('input.skillPlaceholder', { name: availableSkills.find(s => s.id === activeSkillId)?.name || 'coach' }) : t('input.defaultPlaceholder')}
                onImageSelect={handleImageSelect}
                selectedImage={selectedImagePreview}
                onImageRemove={handleImageRemove}
                showRotatingPlaceholder={messages.length === 0 && !activeSkillId}
                onActionSelect={handleActionSelect}
                selectedAgent={selectedAgent}
                onAgentChange={onAgentChange}
                hasMessages={messages.length > 0}
                messageCount={messages.length}
              />
            )}
          </InputBarWrapper>
        )
      )}
    </Container>

      {confirmDialog && (
        <DialogOverlay onClick={() => setConfirmDialog(null)}>
          <DialogBox onClick={(e) => e.stopPropagation()}>
            <DialogTitle>{confirmDialog.title}</DialogTitle>
            <DialogMessage>{confirmDialog.message}</DialogMessage>
            <DialogActions>
              <DialogButton onClick={() => setConfirmDialog(null)}>
                {lang === 'tr' ? 'Vazgeç' : 'Cancel'}
              </DialogButton>
              {confirmDialog.onView && (
                <DialogButton onClick={confirmDialog.onView}>
                  {confirmDialog.viewLabel || (lang === 'tr' ? 'Gör' : 'View')}
                </DialogButton>
              )}
              <DialogButton $variant={confirmDialog.variant || 'default'} onClick={confirmDialog.onConfirm}>
                {confirmDialog.confirmLabel || 'OK'}
              </DialogButton>
            </DialogActions>
          </DialogBox>
        </DialogOverlay>
      )}
    </>
  );
};
